/*
    Desktop nav dropdown.

    Zelfde opzet als de mobiele nav (components/nav.js): geen Alpine, geen GSAP,
    puur data-attributen omzetten en de CSS de transitie laten doen.
    De panelen zitten binnen de nav-kaart, waardoor de kaart zelf naar beneden
    open klapt en de pointer van het item naar het paneel kan zonder te sluiten.
*/

const CLOSE_DELAY = 100
// Gelijk aan de grid-transitie in css/components/nav-dropdown.css
const SLIDE_DURATION = 600

const initNavDropdown = () => {
    const card = document.querySelector('[data-nav-card]')
    if (!card) return

    const region = card.querySelector('[data-nav-region]')
    const panels = card.querySelectorAll('[data-nav-panel]')
    if (!region || panels.length === 0) return

    let closeTimer = null
    let hideTimer = null
    let activeId = null

    const panelFor = (id) => card.querySelector(`[data-nav-panel="${id}"]`)
    const controlFor = (id) => {
        const item = card.querySelector(`[data-nav-item="${id}"]`)
        return item ? item.querySelector('a, [data-nav-trigger]') : null
    }

    // De link-macro kan geen extra attributen meegeven, dus aria hier zetten
    card.querySelectorAll('[data-nav-item]').forEach((item) => {
        const control = item.querySelector('a, [data-nav-trigger]')
        if (!control) return
        control.setAttribute('aria-expanded', 'false')
        control.setAttribute(
            'aria-controls',
            `nav-dropdown-${item.dataset.navItem}`
        )
    })

    const open = (id) => {
        clearTimeout(closeTimer)
        clearTimeout(hideTimer)
        if (activeId === id || !panelFor(id)) return

        const previous = controlFor(activeId)
        if (previous) previous.setAttribute('aria-expanded', 'false')

        panels.forEach((panel) => {
            panel.hidden = panel.dataset.navPanel !== id
        })

        activeId = id
        region.inert = false
        card.setAttribute('data-nav-dropdown-status', 'active')

        const control = controlFor(id)
        if (control) control.setAttribute('aria-expanded', 'true')
    }

    const close = () => {
        clearTimeout(closeTimer)
        if (activeId === null) return

        const control = controlFor(activeId)
        if (control) control.setAttribute('aria-expanded', 'false')

        activeId = null
        card.setAttribute('data-nav-dropdown-status', 'not-active')
        region.inert = true

        // Het paneel pas verbergen na de transitie: zonder inhoud valt er niets
        // meer dicht te klappen en springt de kaart hard toe.
        hideTimer = setTimeout(() => {
            if (activeId !== null) return
            panels.forEach((panel) => {
                panel.hidden = true
            })
        }, SLIDE_DURATION)
    }

    const scheduleClose = (delay = CLOSE_DELAY) => {
        clearTimeout(closeTimer)
        closeTimer = setTimeout(close, delay)
    }

    // Beweegt de cursor naar het paneel toe? Dat klapt nog open, dus de cursor
    // kan al in het gebied staan waar het paneel straks komt. offsetHeight is
    // de volle hoogte, ook al is de regio nog dichtgeklapt.
    const movesTowardsPanel = (e, id) => {
        const panel = panelFor(id)
        if (!panel) return false
        const box = region.getBoundingClientRect()
        return (
            e.clientX >= box.left &&
            e.clientX <= box.right &&
            e.clientY >= box.top - 4 &&
            e.clientY <= box.top + panel.offsetHeight
        )
    }

    // Alleen het parent-item zelf en zijn eigen paneel houden de dropdown
    // open; alles daarbuiten sluit hem. De korte vertraging overbrugt het
    // stukje nav-rij tussen het item en de bovenkant van het paneel.
    card.querySelectorAll('[data-nav-item]').forEach((item) => {
        const id = item.dataset.navItem
        item.addEventListener('mouseenter', () => open(id))
        item.addEventListener('mouseleave', (e) => {
            // Naar het paneel toe: wachten tot het de cursor bereikt heeft, dan
            // vangt de mouseenter van het paneel het op.
            scheduleClose(
                movesTowardsPanel(e, id) ? SLIDE_DURATION : CLOSE_DELAY
            )
        })
    })

    panels.forEach((panel) => {
        panel.addEventListener('mouseenter', () => clearTimeout(closeTimer))
        panel.addEventListener('mouseleave', () => scheduleClose())
    })

    card.addEventListener('focusin', (e) => {
        const item = e.target.closest('[data-nav-item]')
        if (item) {
            open(item.dataset.navItem)
            return
        }
        if (e.target.closest('[data-nav-panel]')) {
            clearTimeout(closeTimer)
            return
        }
        close()
    })

    card.addEventListener('focusout', (e) => {
        if (!card.contains(e.relatedTarget)) scheduleClose()
    })

    // Parent zonder eigen doel rendert een <button>: die toggelt op klik
    card.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-nav-trigger]')
        if (!trigger) return
        const item = trigger.closest('[data-nav-item]')
        if (!item) return
        if (activeId === item.dataset.navItem) {
            close()
        } else {
            open(item.dataset.navItem)
        }
    })

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || activeId === null) return
        const control = controlFor(activeId)
        close()
        if (control) control.focus()
    })
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNavDropdown)
} else {
    initNavDropdown()
}
